import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { planLimitRecentlyShown } from './design-system/plan/plan-limit-store';
import { UPGRADE_PATH } from './design-system/plan/plan-limits';

interface UpgradeNoticeSnackbarProps {
  open: boolean;
  message: string;
  onClose: () => void;
}

const AUTO_HIDE_MS = 8000;

/**
 * Consistent "feature blocked by current plan" toast used everywhere a
 * vendor's action is blocked by a plan cap. Matches the visual treatment of
 * the leads-page banner: a warning surface with a "View plans" CTA that
 * navigates to the upgrade page.
 *
 * When the request layer has just shown the plan-limit dialog for the same
 * refusal (any 403 carrying `quotaType`), this steps aside: one refusal is
 * said once.
 *
 * Why a shared component: every plan-gated surface needs the same wording
 * style and the same upgrade CTA. Inlining the toast in each page led
 * to drift (different durations, different button labels, missing CTAs).
 * One component = one source of truth.
 *
 * It stays a controlled component rather than an imperative `toast()` call:
 * the callers own the `open` flag, and turning that inside out would mean
 * touching every one of them.
 */
export const UpgradeNoticeSnackbar: React.FC<UpgradeNoticeSnackbarProps> = ({
  open,
  message,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // The dialog already said it: close quietly so the caller's state stays honest.
  const superseded = open && planLimitRecentlyShown();

  useEffect(() => {
    if (!open) return;
    if (superseded) {
      onClose();
      return;
    }
    const timer = window.setTimeout(onClose, AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [open, onClose, superseded]);

  if (!open || superseded) return null;

  return (
    <div
      role="alert"
      className="animate-in fade-in slide-in-from-bottom-4 fixed inset-x-4 bottom-6 z-50 mx-auto flex w-fit max-w-[calc(100%-2rem)] items-center gap-3 border border-warning/40 bg-warning/10 px-3 py-2 text-sm shadow-sm"
    >
      <span>{message}</span>
      <Button
        variant="ghost"
        size="sm"
        className="text-warning hover:bg-warning/10 hover:text-warning"
        onClick={() => {
          onClose();
          navigate(UPGRADE_PATH);
        }}
      >
        {t('PLANS.LOCKED.UPGRADE_CTA', 'View plans')} →
      </Button>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('COMMON.CLOSE', 'Close')}
        className="text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

export default UpgradeNoticeSnackbar;
